import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useQuery, useQueryClient } from 'react-query'
import {
  Box,
  Flex,
  Heading,
  Text,
  Button,
  Input,
  Select,
  Checkbox,
  Textarea,
  FormControl,
  FormLabel,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Alert,
  AlertIcon,
  Spinner,
  useToast
} from '@chakra-ui/react'
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend
} from 'recharts'
import { saveAs } from 'file-saver'
import Axios from '../../Axios'
import grades from '../../Utils/SelectList/GradesCarabineros'

export const billingRoles = [
  'ADMINISTRADOR',
  'FINANZAS',
  'DEPTO_CONTROL_GESTION_SOCIAL',
  'GERENCIA',
  'PRESIDENCIA',
  'REVISION'
]
const writers = ['ADMINISTRADOR', 'FINANZAS', 'DEPTO_CONTROL_GESTION_SOCIAL']
export const money = (value) =>
  value == null
    ? 'Sin registro'
    : new Intl.NumberFormat('es-CL', {
        style: 'currency',
        currency: 'CLP',
        maximumFractionDigits: 0
      }).format(value)
const labels = {
  PENDING: 'Pendiente de respuesta',
  PAID: 'Cobrado',
  PARTIAL: 'Cobro parcial',
  OVERPAID: 'Cobro mayor al esperado',
  REJECTED: 'No cobrado',
  UNEXPECTED: 'Sin esperado registrado'
}
const colors = {
  PENDING: 'gray',
  PAID: 'green',
  PARTIAL: 'orange',
  OVERPAID: 'purple',
  REJECTED: 'red',
  UNEXPECTED: 'blue'
}
const get = async (url, params) =>
  (await Axios.get(`/billing/${url}`, { params })).data
const Loading = ({ query }) =>
  query.isLoading ? (
    <Flex p={6} gap={3}>
      <Spinner />
      <Text>Cargando información…</Text>
    </Flex>
  ) : query.isError ? (
    <Alert status="error">
      <AlertIcon />
      {query.error?.response?.data?.message ||
        'No se pudo cargar la información.'}
      <Button ml={3} onClick={() => query.refetch()}>
        Reintentar
      </Button>
    </Alert>
  ) : null
const TableBox = ({ children }) => (
  <Box overflowX="auto" borderWidth="1px" borderRadius="lg" mt={4}>
    {children}
  </Box>
)
const Pager = ({ page, setPage, total = 0 }) => (
  <Flex justify="space-between" align="center" mt={4}>
    <Text fontSize="sm">
      {total.toLocaleString('es-CL')} registros · Página {page + 1}
    </Text>
    <Flex gap={2}>
      <Button size="sm" isDisabled={!page} onClick={() => setPage(page - 1)}>
        Anterior
      </Button>
      <Button
        size="sm"
        isDisabled={(page + 1) * 50 >= total}
        onClick={() => setPage(page + 1)}
      >
        Siguiente
      </Button>
    </Flex>
  </Flex>
)

export const CollectionTable = ({ rows = [], showPeriod = false }) => (
  <TableBox>
    <Table size="sm">
      <Thead>
        <Tr>
          {showPeriod && <Th>Período</Th>}
          <Th>Socio</Th>
          <Th isNumeric>Esperado</Th>
          <Th isNumeric>Cobrado</Th>
          <Th isNumeric>Diferencia</Th>
          <Th>Resultado</Th>
          <Th>Motivo / rango sugerido</Th>
        </Tr>
      </Thead>
      <Tbody>
        {!rows.length && (
          <Tr>
            <Td colSpan={showPeriod ? 7 : 6}>
              No hay registros para esta selección.
            </Td>
          </Tr>
        )}
        {rows.map((row, i) => (
          <Tr key={`${row.period || ''}-${row.rut}-${i}`}>
            {showPeriod && <Td whiteSpace="nowrap">{row.period}</Td>}
            <Td>
              <Text fontWeight="semibold">
                {row.partner ? (
                  <Link to={`/partners/profile/${row.partner}/Perfil de Socio`}>
                    {row.name}
                  </Link>
                ) : (
                  row.name
                )}
              </Text>
              <Text fontSize="xs" color="gray.600">
                {row.rut}
              </Text>
            </Td>
            <Td isNumeric>{money(row.expected)}</Td>
            <Td isNumeric>
              {row.actual ? money(row.actual.amount) : 'Sin respuesta'}
            </Td>
            <Td isNumeric>
              {row.difference == null ? '—' : money(row.difference)}
            </Td>
            <Td>
              <Badge colorScheme={colors[row.status]} whiteSpace="normal">
                {labels[row.status]}
              </Badge>
              {row.actual?.legacy && <Text fontSize="xs">Histórico</Text>}
            </Td>
            <Td>
              <Text fontSize="sm">{row.actual?.reason || '—'}</Text>
              {row.suggestedGrades?.length > 0 && (
                <Text fontSize="xs" color="blue.700">
                  Por monto: {row.suggestedGrades.join(', ')}. Pendiente de
                  confirmar.
                </Text>
              )}
            </Td>
          </Tr>
        ))}
      </Tbody>
    </Table>
  </TableBox>
)

export function PartnerBillingHistory({ partnerId }) {
  const query = useQuery(
    ['billing', 'history', partnerId],
    () => get(`history/${partnerId}`),
    { enabled: Boolean(partnerId), retry: false }
  )
  return (
    <Box>
      <Text color="gray.600">
        Comparación mensual y archivos de respuesta. Los descuentos antiguos
        siguen disponibles en la pestaña Descuentos.
      </Text>
      <Loading query={query} />
      <CollectionTable rows={query.data?.history} showPeriod />
      <Heading size="sm" mt={6}>
        Trazabilidad de respuestas
      </Heading>
      <TableBox>
        <Table size="sm">
          <Thead>
            <Tr>
              <Th>Período</Th>
              <Th>Archivo</Th>
              <Th isNumeric>Cobrado</Th>
              <Th>Resultado / corrección</Th>
            </Tr>
          </Thead>
          <Tbody>
            {query.data?.revisions?.map((r) => (
              <Tr key={r._id}>
                <Td>{r.period}</Td>
                <Td>
                  {r.source?.filename}
                  <Text fontSize="xs">
                    {r.source?.appliedAt &&
                      new Date(r.source.appliedAt).toLocaleString('es-CL')}
                  </Text>
                </Td>
                <Td isNumeric>{money(r.amount)}</Td>
                <Td>
                  {r.outcome === 'REJECTED' ? 'No cobrado' : 'Aplicado'}
                  <Text fontSize="xs">
                    {r.reason || r.source?.correctionReason}
                  </Text>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableBox>
    </Box>
  )
}

const Billing = () => {
  const { user } = useSelector((s) => s.auth)
  const canWrite = writers.includes(user?.role)
  const toast = useToast()
  const cache = useQueryClient()
  const [month, setMonth] = useState(() =>
    new Date()
      .toLocaleDateString('sv-SE', { timeZone: 'America/Santiago' })
      .slice(0, 7)
  )
  const [tab, setTab] = useState(0)
  const [busy, setBusy] = useState(false)
  const [page, setPage] = useState(0)
  const [search, setSearch] = useState('')
  const [issues, setIssues] = useState(false)
  const [cessation, setCessation] = useState('')
  const [confirmedCessation, setConfirmedCessation] = useState(false)
  const [status, setStatus] = useState('')
  const [file, setFile] = useState(null)
  const [confirmAmount, setConfirmAmount] = useState(false)
  const [preview, setPreview] = useState(null)
  const [reason, setReason] = useState('')
  const [rate, setRate] = useState({
    grade: '',
    amount: '',
    from: '',
    enabled: true,
    note: ''
  })
  const validMonth = /^20\d{2}-(0[1-9]|1[0-2])$/.test(month)
  const prepare = useQuery(
    ['billing', 'prepare', month, page, search, issues],
    () => get('prepare', { period: month, page, search, issues }),
    { enabled: tab === 0 && validMonth, retry: false, keepPreviousData: false }
  )
  const batches = useQuery(
    ['billing', 'batches', month],
    () => get('batches', { period: month }),
    { enabled: validMonth && tab === 0, retry: false }
  )
  const rates = useQuery(['billing', 'rates'], () => get('rates'), {
    enabled: tab === 1,
    retry: false
  })
  const periods = useQuery(
    ['billing', 'periods', month],
    () => get('periods', { period: month }),
    { enabled: tab === 2, retry: false }
  )
  const reconciliation = useQuery(
    ['billing', 'reconciliation', month, page, search, status],
    () => get('reconciliation', { period: month, page, search, status }),
    { enabled: tab === 2 && validMonth, retry: false }
  )
  const charts = useQuery(
    ['billing', 'charts', month],
    () => get('charts', { to: month, months: 12 }),
    { enabled: tab === 3 && validMonth, retry: false, staleTime: 60000 }
  )
  const action = async (fn, success) => {
    setBusy(true)
    try {
      const value = await fn()
      cache.invalidateQueries(['billing'])
      if (success)
        toast({ title: success, status: 'success', isClosable: true })
      return value
    } catch (e) {
      let message = e.response?.data?.message
      if (e.response?.data instanceof Blob) {
        try {
          message = JSON.parse(await e.response.data.text()).message
        } catch (_) {}
      }
      toast({
        title: message || 'No se pudo completar la operación.',
        status: 'error',
        duration: 10000,
        isClosable: true
      })
      return null
    } finally {
      setBusy(false)
    }
  }
  const download = (id, format, version) =>
    action(async () => {
      const response = await Axios.get(`/billing/batches/${id}/export`, {
        params: { format },
        responseType: 'blob'
      })
      saveAs(response.data, `nomina-${month}-v${version}.${format}`)
    })
  const upload = () =>
    action(async () => {
      const body = new FormData()
      body.append('file', file)
      body.append('period', month)
      body.append('confirmReportedAmount', String(confirmAmount))
      const response = await Axios.post('/billing/imports/preview', body)
      setPreview(response.data)
      setReason('')
    })
  const changeMonth = (value) => {
    setMonth(value)
    setPage(0)
    setPreview(null)
    setConfirmedCessation(false)
    setCessation('')
  }
  const summary = reconciliation.data?.summary
  const metrics = charts.data || []
  return (
    <Box px={{ base: 0, lg: 6 }} pb={10}>
      <Flex align="center" justify="space-between" gap={4} wrap="wrap" mb={6}>
        <Box>
          <Heading size="lg" color="green.800">
            Cobros de socios
          </Heading>
          <Text mt={2} color="gray.600">
            Nóminas, tarifas y seguimiento de los descuentos mensuales.
          </Text>
        </Box>
        <FormControl maxW="220px">
          <FormLabel htmlFor="billing-period">Período de cobro</FormLabel>
          <Input
            id="billing-period"
            type="month"
            value={month}
            onChange={(e) => changeMonth(e.target.value)}
          />
        </FormControl>
      </Flex>
      <Tabs
        colorScheme="green"
        index={tab}
        onChange={(value) => {
          setTab(value)
          setPage(0)
          setSearch('')
        }}
        isLazy
      >
        <TabList overflowX="auto">
          <Tab whiteSpace="nowrap">Nómina mensual</Tab>
          <Tab whiteSpace="nowrap">Rangos y tarifas</Tab>
          <Tab whiteSpace="nowrap">Resultados de cobro</Tab>
          <Tab>Evolución</Tab>
        </TabList>
        <TabPanels>
          <TabPanel px={0}>
            <Alert status="info" borderRadius="lg">
              <AlertIcon />
              <Text>
                Los socios se incluyen desde su mes de inscripción. Sin tarifa
                confirmada, se usa el último cobro real como referencia inicial.
                Los montos no cobrados no se acumulan.
              </Text>
            </Alert>
            {prepare.data?.locked && (
              <Alert status="info" mt={3}>
                <AlertIcon />
                <Text>
                  Este período ya tiene resultados de cobro. Se muestra la
                  nómina conservada; los cambios de tarifas o fichas no
                  modifican el esperado histórico. Si no se generó una nómina,
                  el esperado permanece sin registro.
                </Text>
              </Alert>
            )}
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} my={5}>
              {[
                ['Socios preparados', prepare.data?.count],
                ['Total esperado', money(prepare.data?.total)],
                ['Pendientes de corregir', prepare.data?.issueCount]
              ].map(([label, value]) => (
                <Stat key={label} borderWidth="1px" p={4} borderRadius="lg">
                  <StatLabel>{label}</StatLabel>
                  <StatNumber fontSize="2xl">{value ?? '—'}</StatNumber>
                </Stat>
              ))}
            </SimpleGrid>
            <Flex gap={4} wrap="wrap" align="center">
              <Input
                aria-label="Buscar socio en preparación"
                placeholder="Buscar por nombre, RUT o código"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value)
                  setPage(0)
                }}
                maxW="400px"
              />
              <Checkbox
                isChecked={issues}
                onChange={(e) => {
                  setIssues(e.target.checked)
                  setPage(0)
                }}
              >
                Ver pendientes de corregir
              </Checkbox>
            </Flex>
            <Loading query={prepare} />
            <TableBox>
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th>Socio</Th>
                    <Th>RUT</Th>
                    <Th>{issues ? 'Pendiente' : 'Origen del monto'}</Th>
                    <Th isNumeric>{issues ? 'Acción' : 'Cuota'}</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {prepare.data?.rows?.map((row) => (
                    <Tr key={row.partner}>
                      <Td>{row.name}</Td>
                      <Td>{row.rut}</Td>
                      <Td>
                        {issues
                          ? row.reason
                          : row.source === 'RATE'
                          ? `Tarifa: ${row.grade}`
                          : 'Último cobro real'}
                      </Td>
                      <Td isNumeric>
                        {issues ? (
                          <Button
                            as={Link}
                            size="xs"
                            to={`/partners/profile/${row.partner}/Perfil de Socio`}
                          >
                            Revisar ficha
                          </Button>
                        ) : (
                          money(row.amount)
                        )}
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableBox>
            <Pager
              page={page}
              setPage={setPage}
              total={prepare.data?.totalRecords}
            />
            {canWrite && (
              <Box borderWidth="1px" borderRadius="lg" p={5} mt={6}>
                <Heading size="sm">Guardar nómina para entrega</Heading>
                <Text fontSize="sm" color="gray.600" my={3}>
                  Se conserva una versión con los montos de este momento. Podrás
                  descargarla en ambos formatos. Una vez recibidos resultados,
                  el esperado queda protegido.
                </Text>
                <Flex align="end" gap={4} wrap="wrap">
                  <FormControl maxW="240px">
                    <FormLabel htmlFor="cessation">Período de cese</FormLabel>
                    <Input
                      id="cessation"
                      type="month"
                      min={month}
                      value={cessation}
                      onChange={(e) => {
                        setCessation(e.target.value)
                        setConfirmedCessation(false)
                      }}
                    />
                  </FormControl>
                  <Button
                    colorScheme="green"
                    isLoading={busy}
                    isDisabled={
                      !validMonth ||
                      prepare.data?.locked ||
                      !prepare.data?.count ||
                      prepare.data?.issueCount > 0 ||
                      !confirmedCessation ||
                      !cessation ||
                      prepare.isFetching
                    }
                    onClick={() =>
                      action(
                        () =>
                          Axios.post('/billing/batches', {
                            period: month,
                            cessation
                          }),
                        'Nómina guardada'
                      )
                    }
                  >
                    Generar versión
                  </Button>
                </Flex>
                <Checkbox
                  mt={3}
                  isChecked={confirmedCessation}
                  onChange={(e) => setConfirmedCessation(e.target.checked)}
                >
                  Confirmo el período de cese que corresponde al envío acordado
                  con Finanzas.
                </Checkbox>
              </Box>
            )}
            <Heading size="md" mt={7}>
              Versiones disponibles
            </Heading>
            <Loading query={batches} />
            <TableBox>
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th>Versión</Th>
                    <Th>Generada</Th>
                    <Th isNumeric>Socios</Th>
                    <Th isNumeric>Total</Th>
                    <Th>Descargar</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {batches.data?.map((b) => (
                    <Tr key={b._id}>
                      <Td>{b.version}</Td>
                      <Td>{new Date(b.createdAt).toLocaleString('es-CL')}</Td>
                      <Td isNumeric>{b.count}</Td>
                      <Td isNumeric>{money(b.total)}</Td>
                      <Td>
                        <Flex gap={2}>
                          <Button
                            size="sm"
                            isDisabled={busy}
                            onClick={() => download(b._id, 'xlsx', b.version)}
                          >
                            Excel
                          </Button>
                          <Button
                            size="sm"
                            isDisabled={busy}
                            onClick={() => download(b._id, 'txt', b.version)}
                          >
                            TXT
                          </Button>
                        </Flex>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableBox>
          </TabPanel>
          <TabPanel px={0}>
            <Text color="gray.600" mb={5}>
              Cada cambio conserva una versión y su mes de vigencia. Las nóminas
              ya generadas mantienen sus montos originales.
            </Text>
            {canWrite && (
              <Box
                as="form"
                onSubmit={(e) => {
                  e.preventDefault()
                  action(async () => {
                    await Axios.post('/billing/rates', {
                      ...rate,
                      amount: Number(rate.amount)
                    })
                    setRate({
                      grade: '',
                      amount: '',
                      from: '',
                      enabled: true,
                      note: ''
                    })
                  }, 'Versión de tarifa guardada')
                }}
                borderWidth="1px"
                borderRadius="lg"
                p={5}
              >
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                  <FormControl isRequired>
                    <FormLabel htmlFor="rate-grade">Rango / grado</FormLabel>
                    <Input
                      id="rate-grade"
                      list="billing-grades"
                      value={rate.grade}
                      onChange={(e) =>
                        setRate({ ...rate, grade: e.target.value })
                      }
                    />
                    <datalist id="billing-grades">
                      {[
                        ...new Set([
                          ...grades.filter((g) => g !== 'Sin definir'),
                          ...(rates.data || []).map((r) => r.grade)
                        ])
                      ].map((g) => (
                        <option key={g} value={g} />
                      ))}
                    </datalist>
                  </FormControl>
                  <FormControl isRequired>
                    <FormLabel htmlFor="rate-amount">
                      Cuota mensual en pesos
                    </FormLabel>
                    <Input
                      id="rate-amount"
                      type="number"
                      min="1"
                      max="9999999"
                      step="1"
                      isDisabled={!rate.enabled}
                      value={rate.amount}
                      onChange={(e) =>
                        setRate({ ...rate, amount: e.target.value })
                      }
                    />
                  </FormControl>
                  <FormControl isRequired>
                    <FormLabel htmlFor="rate-from">Vigente desde</FormLabel>
                    <Input
                      id="rate-from"
                      type="month"
                      value={rate.from}
                      onChange={(e) =>
                        setRate({ ...rate, from: e.target.value })
                      }
                    />
                  </FormControl>
                </SimpleGrid>
                <FormControl mt={3}>
                  <FormLabel htmlFor="rate-note">Motivo del cambio</FormLabel>
                  <Input
                    id="rate-note"
                    maxLength={500}
                    value={rate.note}
                    onChange={(e) => setRate({ ...rate, note: e.target.value })}
                  />
                </FormControl>
                <Flex mt={4} gap={4} wrap="wrap">
                  <Checkbox
                    isChecked={rate.enabled}
                    onChange={(e) =>
                      setRate({ ...rate, enabled: e.target.checked })
                    }
                  >
                    Tarifa habilitada
                  </Checkbox>
                  <Button type="submit" colorScheme="green" isLoading={busy}>
                    Guardar nueva versión
                  </Button>
                </Flex>
              </Box>
            )}
            <Loading query={rates} />
            <TableBox>
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th>Rango</Th>
                    <Th>Desde</Th>
                    <Th isNumeric>Monto</Th>
                    <Th>Estado</Th>
                    <Th>Motivo</Th>
                    {canWrite && <Th>Acción</Th>}
                  </Tr>
                </Thead>
                <Tbody>
                  {rates.data?.map((r) => (
                    <Tr key={r._id}>
                      <Td>{r.grade}</Td>
                      <Td>{r.from}</Td>
                      <Td isNumeric>{money(r.amount)}</Td>
                      <Td>{r.enabled ? 'Habilitada' : 'Deshabilitada'}</Td>
                      <Td>{r.note || '—'}</Td>
                      {canWrite && (
                        <Td>
                          <Button
                            size="xs"
                            onClick={() =>
                              setRate({
                                grade: r.grade,
                                amount: r.amount,
                                from: month,
                                enabled: r.enabled,
                                note: ''
                              })
                            }
                          >
                            Nueva vigencia
                          </Button>
                        </Td>
                      )}
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableBox>
          </TabPanel>
          <TabPanel px={0}>
            {canWrite && (
              <Box borderWidth="1px" borderRadius="lg" p={5} mb={5}>
                <Heading size="sm">Importar respuesta de Carabineros</Heading>
                <Text my={3} fontSize="sm">
                  Selecciona el período correcto y carga un Excel o TXT. Primero
                  revisa el resultado; los registros se actualizan al aplicar la
                  respuesta.
                </Text>
                <Flex gap={3} wrap="wrap">
                  <Input
                    aria-label="Archivo de respuesta"
                    type="file"
                    accept=".xlsx,.txt"
                    p={1}
                    maxW="450px"
                    onChange={(e) => {
                      setFile(e.target.files?.[0] || null)
                      setPreview(null)
                      setConfirmAmount(false)
                    }}
                  />
                  <Button
                    isLoading={busy}
                    isDisabled={!file || !validMonth}
                    onClick={upload}
                  >
                    Revisar archivo
                  </Button>
                </Flex>
                {file?.name.toLowerCase().endsWith('.txt') && (
                  <Checkbox
                    mt={3}
                    isChecked={confirmAmount}
                    onChange={(e) => setConfirmAmount(e.target.checked)}
                  >
                    Finanzas confirmó que «monto informado» corresponde al cobro
                    real en este TXT de aplicados.
                  </Checkbox>
                )}
                {preview && (
                  <Box mt={4}>
                    <Alert status={preview.errors?.length ? 'error' : 'info'}>
                      <AlertIcon />
                      <Text>
                        {preview.filename}: {preview.count} registros,{' '}
                        {money(preview.total)} cobrados, {preview.rejected}{' '}
                        rechazados, {preview.duplicates} duplicados idénticos
                        omitidos. {preview.conflicts} conflictos y{' '}
                        {preview.unmatched} sin socio asociado.
                      </Text>
                    </Alert>
                    {preview.unmatched > 0 && (
                      <Text mt={2} fontSize="sm">
                        Los resultados sin socio se conservarán para revisión.
                        Registra su ficha con el RUT correspondiente para
                        asociarlos.
                      </Text>
                    )}
                    {preview.errors?.map((e, i) => (
                      <Text color="red.700" fontSize="sm" key={i}>
                        Fila {e.line}: {e.message}
                      </Text>
                    ))}
                    {preview.conflicts > 0 && (
                      <>
                        <Text mt={3}>
                          Las siguientes filas cambiarían resultados anteriores:
                        </Text>
                        {preview.conflictRows?.slice(0, 10).map((r) => (
                          <Text fontSize="sm" key={r._id}>
                            {r.rut}: {money(r.previousAmount)} →{' '}
                            {money(r.amount)}
                          </Text>
                        ))}
                        <Textarea
                          aria-label="Motivo de corrección"
                          mt={3}
                          placeholder="Motivo de la corrección (mínimo 10 caracteres)"
                          maxLength={500}
                          value={reason}
                          onChange={(e) => setReason(e.target.value)}
                        />
                      </>
                    )}
                    {preview.state === 'APPLIED' ? (
                      <Text mt={3} color="green.700">
                        Esta respuesta ya fue aplicada. No se duplicaron
                        registros.
                      </Text>
                    ) : (
                      <Button
                        mt={3}
                        colorScheme="green"
                        isLoading={busy}
                        isDisabled={
                          preview.errors?.length > 0 ||
                          (preview.conflicts > 0 && reason.trim().length < 10)
                        }
                        onClick={() =>
                          action(async () => {
                            await Axios.post(
                              `/billing/imports/${preview._id}/apply`,
                              { correctionReason: reason }
                            )
                            setPreview(null)
                            setFile(null)
                          }, 'Respuesta aplicada')
                        }
                      >
                        Aplicar respuesta revisada
                      </Button>
                    )}
                  </Box>
                )}
              </Box>
            )}
            <SimpleGrid columns={{ base: 2, lg: 4 }} spacing={3} mb={5}>
              {[
                ['Esperado', money(summary?.expected)],
                ['Cobrado', money(summary?.actual)],
                ['No cobrado confirmado', money(summary?.notCollected)],
                ['Pendiente de respuesta', money(summary?.pendingAmount)]
              ].map(([label, value]) => (
                <Stat key={label} borderWidth="1px" borderRadius="lg" p={4}>
                  <StatLabel>{label}</StatLabel>
                  <StatNumber fontSize="xl">{value}</StatNumber>
                </Stat>
              ))}
            </SimpleGrid>
            <Flex gap={3} wrap="wrap">
              <Input
                maxW="350px"
                aria-label="Buscar resultado de cobro"
                placeholder="Nombre, RUT o código"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value)
                  setPage(0)
                }}
              />
              <Select
                maxW="280px"
                aria-label="Filtrar por resultado"
                value={status}
                onChange={(e) => {
                  setStatus(e.target.value)
                  setPage(0)
                }}
              >
                <option value="">Todos los resultados</option>
                {Object.entries(labels).map(([key, label]) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
              </Select>
            </Flex>
            <Loading query={reconciliation} />
            <CollectionTable rows={reconciliation.data?.rows} />
            <Pager
              page={page}
              setPage={setPage}
              total={reconciliation.data?.totalRecords}
            />
            <Flex my={5} align="center" gap={3}>
              <Badge
                colorScheme={
                  reconciliation.data?.state?.closedAt ? 'green' : 'gray'
                }
              >
                {reconciliation.data?.state?.closedAt
                  ? 'Período cerrado'
                  : 'Período abierto'}
              </Badge>
              {canWrite && !reconciliation.data?.state?.closedAt && (
                <Button
                  size="sm"
                  isDisabled={busy || !summary || summary.pendingCount > 0}
                  onClick={() => {
                    if (
                      window.confirm(
                        '¿Confirmas que recibiste y revisaste la respuesta completa del período?'
                      )
                    )
                      action(
                        () =>
                          Axios.post(`/billing/periods/${month}/close`, {
                            complete: true
                          }),
                        'Período cerrado'
                      )
                  }}
                >
                  Cerrar período
                </Button>
              )}
              {user?.role === 'ADMINISTRADOR' &&
                reconciliation.data?.state?.closedAt && (
                  <Button
                    size="sm"
                    isDisabled={busy}
                    onClick={() =>
                      action(
                        () => Axios.post(`/billing/periods/${month}/reopen`),
                        'Período reabierto'
                      )
                    }
                  >
                    Reabrir para corregir
                  </Button>
                )}
            </Flex>
            <Heading size="sm" mt={7}>
              Archivos del período
            </Heading>
            <Loading query={periods} />
            <TableBox>
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th>Archivo</Th>
                    <Th>Estado</Th>
                    <Th>Registros</Th>
                    <Th>Importado</Th>
                    <Th>Acciones</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {periods.data?.imports
                    ?.filter((i) => i.period === month)
                    .map((i) => (
                      <Tr key={i._id}>
                        <Td>{i.filename}</Td>
                        <Td>
                          {
                            {
                              APPLIED: 'Aplicado',
                              PREVIEW: 'Por revisar',
                              PREPARING: 'Preparando',
                              FAILED: 'Error'
                            }[i.state]
                          }
                        </Td>
                        <Td>{i.count}</Td>
                        <Td>{new Date(i.createdAt).toLocaleString('es-CL')}</Td>
                        <Td>
                          <Flex gap={2}>
                            {canWrite && i.state === 'PREVIEW' && (
                              <Button
                                size="xs"
                                onClick={() =>
                                  action(async () => {
                                    setPreview(await get(`imports/${i._id}`))
                                    setReason('')
                                  })
                                }
                              >
                                Revisar
                              </Button>
                            )}
                            <Button
                              size="xs"
                              onClick={() =>
                                action(async () => {
                                  const response = await Axios.get(
                                    `/billing/imports/${i._id}/file`,
                                    { responseType: 'blob' }
                                  )
                                  saveAs(response.data, i.filename)
                                })
                              }
                            >
                              Original
                            </Button>
                          </Flex>
                        </Td>
                      </Tr>
                    ))}
                </Tbody>
              </Table>
            </TableBox>
          </TabPanel>
          <TabPanel px={0}>
            <Text mb={5} color="gray.600">
              Últimos 12 meses. Los períodos sin nómina conservada muestran solo
              los cobros reales. Pendiente de respuesta no equivale a rechazo.
            </Text>
            <Loading query={charts} />
            <Box h="340px" borderWidth="1px" borderRadius="lg" p={3}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={metrics}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="period" />
                  <YAxis tickFormatter={(v) => `${Math.round(v / 1000)} mil`} />
                  <Tooltip formatter={(v) => money(v)} />
                  <Legend />
                  <Bar dataKey="expected" name="Esperado" fill="#3b6d3b" />
                  <Bar dataKey="actual" name="Cobrado" fill="#83b678" />
                  <Bar
                    dataKey="notCollected"
                    name="No cobrado confirmado"
                    fill="#d77957"
                  />
                </BarChart>
              </ResponsiveContainer>
            </Box>
            <Box h="300px" mt={6}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={metrics}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="period" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  <Bar
                    dataKey="actualCount"
                    name="Socios con cobro"
                    fill="#83b678"
                  />
                  <Bar
                    dataKey="rejectedCount"
                    name="Rechazados"
                    fill="#d77957"
                  />
                  <Bar
                    dataKey="pendingCount"
                    name="Pendientes"
                    fill="#a0aec0"
                  />
                </BarChart>
              </ResponsiveContainer>
            </Box>
            <Heading size="sm" mt={5}>
              Motivos de rechazo · {month}
            </Heading>
            <TableBox>
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th>Motivo</Th>
                    <Th isNumeric>Socios</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {Object.entries(
                    metrics.find((m) => m.period === month)?.reasons || {}
                  ).map(([name, count]) => (
                    <Tr key={name}>
                      <Td>{name}</Td>
                      <Td isNumeric>{count}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableBox>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  )
}
export default Billing
